/**
 * Platform-catalog tables (default_roles + units_of_measure).
 *
 * Phase 2 / Plan 02-06: catalog tables that didn't fit cleanly into Wave 1's
 * domain split. Declared here as platform-managed (no tenant_id, no RLS —
 * CONTEXT D-16). The 0001_catalog_tables.sql migration creates them AND adds
 * REVOKE INSERT/UPDATE/DELETE on app_user.
 *
 * Why separate from jobs.ts (which holds job_statuses + job_types):
 *   - jobs.ts already covers two catalogs and three transactional tables;
 *     adding two more would push the file past the readability threshold.
 *   - These catalogs are referenced by tenant_users.role (text + CHECK) and
 *     by jobTypes.unit_of_measure_code (text — no FK to keep lookup fast),
 *     not by FK from any tenant-scoped table — so they don't share a domain.
 *   - Phase 18 admin console will INSERT into these via migrator role.
 *
 * Sources:
 *   - master spec §6 (10-role permission matrix → default_roles)
 *   - master spec §35 (LEAD_DRIVER role → 11th default_role per DEC-011)
 *   - master spec §12 (billing methods → units_of_measure)
 *   - DEC-011 (LEAD_DRIVER role from §35)
 *   - CONTEXT D-16 (platform catalogs: no RLS; REVOKE on app_user)
 *   - RESEARCH Open Question #5 (default_roles as table, not pgEnum)
 *
 * Locked contract:
 *   - default_roles.code is text; tenant_users.role is text + CHECK referencing
 *     these codes (NOT a FK — keeps tenant_users lookup fast).
 *   - units_of_measure.code is text; jobTypes.unit_of_measure_code references
 *     by name; same FK-free pattern.
 *   - No pgPolicy here — D-16 platform-managed catalogs. Read access is via
 *     the GRANT in Plan 02-05's epilogue (which inherits to new tables via
 *     ALTER DEFAULT PRIVILEGES); write access is REVOKEd in 0001 epilogue.
 */

import { pgTable, serial, text, jsonb, timestamp, uniqueIndex } from 'drizzle-orm/pg-core';
import { createInsertSchema, createSelectSchema } from 'drizzle-zod';

/**
 * default_roles — 11-row metadata catalog per master spec §6 (10) + §35 (LEAD_DRIVER).
 *
 * NO tenant_id, NO RLS (D-16 platform-managed catalog). Plan 02-06 seeds
 * exactly 11 rows by iterating TENANT_USER_ROLES from enums.ts.
 *
 * `code` matches the TENANT_USER_ROLES literal-string union — UNIQUE'd below.
 * `permission_set` is a forward-compat JSONB column; Phase 2 seeds with `{}`,
 * Phase 18 admin console populates with master-spec §6 detailed permission flags.
 *
 * Reference posture: tenant_users.role is `text` with a CHECK constraint
 * referencing TENANT_USER_ROLES (NOT a FK to default_roles.code). This keeps
 * the per-row tenant_users lookup FK-free; default_roles exists for admin-UI
 * rendering (humans need labels + descriptions) and the boot-time assertion
 * (Plan 02-07 reads count = TENANT_USER_ROLES.length).
 */
export const defaultRoles = pgTable(
  'default_roles',
  {
    id: serial('id').primaryKey(),
    // Stable machine code (e.g. 'DRIVER') — matches TENANT_USER_ROLES; UNIQUE'd below.
    code: text('code').notNull(),
    // Human-readable label rendered in admin UI.
    label: text('label').notNull(),
    // Optional long-form description; Phase 18 admin UI may surface this.
    description: text('description'),
    // Forward-compat: Phase 18 fills with master-spec §6 detailed flags.
    permissionSet: jsonb('permission_set').notNull().default({}),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex('default_roles_code_idx').on(t.code)],
);

export type DefaultRole = typeof defaultRoles.$inferSelect;
export type NewDefaultRole = typeof defaultRoles.$inferInsert;
export const defaultRoleSelectSchema = createSelectSchema(defaultRoles);
export const defaultRoleInsertSchema = createInsertSchema(defaultRoles);

/**
 * units_of_measure — 4-row catalog per master spec §12 (billing methods).
 *
 * NO tenant_id, NO RLS (D-16 platform-managed catalog). Plan 02-06 seeds
 * BBL/GAL/LBS/TON; future units can be added via additional seed migrations.
 *
 * `code` matches the codes referenced by jobTypes.unit_of_measure_code (free
 * text, no FK — keeps the jobTypes lookup fast). The four §12 codes split
 * into two `kind` categories: VOLUME (BBL, GAL) and WEIGHT (LBS, TON).
 */
export const unitsOfMeasure = pgTable(
  'units_of_measure',
  {
    id: serial('id').primaryKey(),
    // 'BBL' | 'GAL' | 'LBS' | 'TON' — UNIQUE'd below.
    code: text('code').notNull(),
    // 'Barrel' | 'Gallon' | 'Pound' | 'Ton'.
    label: text('label').notNull(),
    // 'VOLUME' | 'WEIGHT' — master spec §12 categorization.
    kind: text('kind').notNull(),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().defaultNow(),
    updatedAt: timestamp('updated_at', { withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [uniqueIndex('units_of_measure_code_idx').on(t.code)],
);

export type UnitOfMeasure = typeof unitsOfMeasure.$inferSelect;
export type NewUnitOfMeasure = typeof unitsOfMeasure.$inferInsert;
export const unitOfMeasureSelectSchema = createSelectSchema(unitsOfMeasure);
export const unitOfMeasureInsertSchema = createInsertSchema(unitsOfMeasure);
